import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, ScrollText, Trash2 } from 'lucide-react';

// ─── Simulation Config ────────────────────────────────────────────────────────

const LIMIT = 3;       // max requests per window
const WINDOW_SIZE = 5; // window duration (sliding)

// Each event has a timestamp (t) and type.
// The window at time t covers (t - WINDOW_SIZE, t].
// Algorithm: on each request at time t:
//   1. Remove log entries with timestamp <= t - WINDOW_SIZE (expired)
//   2. Count remaining entries
//   3. If count < LIMIT → allow, add t to log
//   4. Else → reject
const RAW_EVENTS = [
  { type: 'init' },
  { type: 'request', t: 1, label: 'GET /api/users' },      // log=[1], count=1, ALLOWED
  { type: 'request', t: 2, label: 'POST /api/item' },       // log=[1,2], count=2, ALLOWED
  { type: 'request', t: 3, label: 'GET /api/data' },        // log=[1,2,3], count=3, at limit, ALLOWED
  { type: 'request', t: 3, label: 'GET /api/search' },      // count=3 = LIMIT, REJECTED
  { type: 'request', t: 3, label: 'DELETE /api/item/5' },   // count=3, REJECTED
  // t=5: cutoff=0, nothing cleaned (1,2,3 all > 0), count=3 → REJECT
  { type: 'request', t: 5, label: 'GET /api/profile' },     // window=(0,5], [1,2,3] all in → REJECTED
  // t=6: cutoff=1, t=1 expires (1 ≤ 1), log shrinks to [2,3], count=2 → ALLOW
  { type: 'request', t: 6, label: 'GET /api/feed' },        // t=1 cleaned → count=2, ALLOWED
  // t=7: cutoff=2, t=2 expires, log=[3,6], count=2 → ALLOW
  { type: 'request', t: 7, label: 'GET /api/stats' },       // t=2 cleaned → count=2, ALLOWED
  // t=8: cutoff=3, t=3 expires, log=[6,7], count=2 → ALLOW
  { type: 'request', t: 8, label: 'POST /api/like' },       // t=3 cleaned → count=2, ALLOWED
  // t=9: cutoff=4, nothing cleaned (6,7,8 all >4), count=3 → REJECT
  { type: 'request', t: 9, label: 'GET /api/data' },        // window=(4,9], [6,7,8] in → REJECTED
  // t=10: cutoff=5, nothing cleaned (6,7,8 all >5), count=3 → REJECT
  { type: 'request', t: 10, label: 'GET /api/users' },      // window=(5,10], [6,7,8] still in → REJECTED
  // t=11: cutoff=6, t=6 expires (6 ≤ 6), log=[7,8], count=2 → ALLOW
  { type: 'request', t: 11, label: 'GET /api/posts' },      // t=6 cleaned → count=2, ALLOWED
  // t=12: cutoff=7, t=7 expires, log=[8,11], count=2 → ALLOW
  { type: 'request', t: 12, label: 'GET /api/burst-1' },    // t=7 cleaned → count=2, ALLOWED
  { type: 'request', t: 12, label: 'GET /api/burst-2' },    // count=3, at limit, REJECTED
  { type: 'request', t: 12, label: 'GET /api/burst-3' },    // count=3, REJECTED
  // t=15: cutoff=10, [8,11,12] all expire (≤10), log=[], fresh window
  { type: 'request', t: 15, label: 'GET /api/normal' },     // all expired → count=0, ALLOWED
];

// ─── Step Engine ──────────────────────────────────────────────────────────────

function buildSteps() {
  const steps = [];
  let log = [];   // array of { id, t, label }
  let nextId = 0;
  let totalRequests = 0;
  let allowedCount = 0;
  let rejectedCount = 0;

  for (const event of RAW_EVENTS) {
    if (event.type === 'init') {
      steps.push({
        id: 0,
        phase: 'init',
        t: 0,
        log: [],
        expired: [],
        windowStart: -WINDOW_SIZE,
        windowEnd: 0,
        totalRequests,
        allowedCount,
        rejectedCount,
        currentEntry: null,
        cleanedEntries: [],
        title: 'Initialize Sliding Window Log',
        badge: null,
        explanation:
          `Sliding Window Log initialized. Unlike Fixed Window, there is no periodic reset. ` +
          `Instead, every request carries a timestamp and is appended to an in-memory log. ` +
          `On each new request at time t, the algorithm: ` +
          `(1) removes all log entries older than t − ${WINDOW_SIZE} (they've "slid out" of the window), ` +
          `(2) counts the remaining entries, ` +
          `(3) allows the request if count < ${LIMIT}, rejects otherwise. ` +
          `This gives precise per-${WINDOW_SIZE}-tick rate limiting with no thundering herd at boundaries. ` +
          `Trade-off: memory scales with request volume — O(requests in window) vs O(1) for Fixed Window.`,
      });
    } else if (event.type === 'request') {
      totalRequests++;
      const cutoff = event.t - WINDOW_SIZE; // entries with t <= cutoff are expired
      const cleaned = log.filter(e => e.t <= cutoff);
      const newLog = log.filter(e => e.t > cutoff);
      const countBeforeAdd = newLog.length;
      const allowed = countBeforeAdd < LIMIT;

      if (allowed) {
        allowedCount++;
        const entry = { id: nextId++, t: event.t, label: event.label };
        log = [...newLog, entry];

        steps.push({
          id: steps.length,
          phase: 'allowed',
          t: event.t,
          log: [...log],
          expired: [],
          windowStart: cutoff,
          windowEnd: event.t,
          totalRequests,
          allowedCount,
          rejectedCount,
          currentEntry: entry,
          cleanedEntries: cleaned,
          title: `Allowed: ${event.label}`,
          badge: cleaned.length > 0 ? `Allowed (+${cleaned.length} expired)` : (countBeforeAdd + 1 === LIMIT ? 'Allowed — Limit Reached' : 'Allowed'),
          explanation:
            `Request "${event.label}" arrives at t=${event.t}. Window = (${cutoff}, ${event.t}]. ` +
            (cleaned.length > 0
              ? `${cleaned.length} expired entr${cleaned.length !== 1 ? 'ies' : 'y'} removed (t ≤ ${cutoff}): ${cleaned.map(e => `t=${e.t}`).join(', ')}. `
              : `No entries expired. `) +
            `Active log has ${countBeforeAdd} entr${countBeforeAdd !== 1 ? 'ies' : 'y'} — under the limit of ${LIMIT}. ` +
            `Request ALLOWED. t=${event.t} appended to log → log now has ${log.length} entr${log.length !== 1 ? 'ies' : 'y'}. ` +
            (log.length === LIMIT
              ? `Log is now at capacity (${LIMIT}/${LIMIT}). The next request will be rejected unless an entry expires first.`
              : `${LIMIT - log.length} more request${LIMIT - log.length !== 1 ? 's' : ''} possible before the window fills.`),
        });
      } else {
        rejectedCount++;
        log = newLog; // still apply cleanup even on reject

        steps.push({
          id: steps.length,
          phase: 'rejected',
          t: event.t,
          log: [...log],
          expired: [],
          windowStart: cutoff,
          windowEnd: event.t,
          totalRequests,
          allowedCount,
          rejectedCount,
          currentEntry: null,
          cleanedEntries: cleaned,
          title: `Rejected: ${event.label}`,
          badge: cleaned.length > 0 ? `Rejected (+${cleaned.length} expired)` : 'Rejected',
          explanation:
            `Request "${event.label}" arrives at t=${event.t}. Window = (${cutoff}, ${event.t}]. ` +
            (cleaned.length > 0
              ? `${cleaned.length} expired entr${cleaned.length !== 1 ? 'ies' : 'y'} removed first: ${cleaned.map(e => `t=${e.t}`).join(', ')}. `
              : `No entries expired. `) +
            `Active log has ${countBeforeAdd} entr${countBeforeAdd !== 1 ? 'ies' : 'y'} — at the limit of ${LIMIT}. ` +
            `Request REJECTED (HTTP 429). No entry is added to the log. ` +
            `Oldest active entry is t=${log[0]?.t}. It will expire when t > ${log[0] ? log[0].t + WINDOW_SIZE : '?'}, ` +
            `after which this client can retry.`,
        });
      }
    }
  }

  return steps;
}

const STEPS = buildSteps();

// ─── Phase Styles ─────────────────────────────────────────────────────────────

const PHASE = {
  init:     { dot: 'bg-gray-400',    text: 'text-gray-600 dark:text-gray-400',       border: 'border-gray-200 dark:border-gray-700',     bg: 'bg-gray-50 dark:bg-gray-900/40',       badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700' },
  allowed:  { dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800', bg: 'bg-emerald-50 dark:bg-emerald-950/40', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' },
  rejected: { dot: 'bg-red-500',     text: 'text-red-600 dark:text-red-400',         border: 'border-red-200 dark:border-red-800',         bg: 'bg-red-50 dark:bg-red-950/40',         badge: 'bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-300 border-red-200 dark:border-red-800' },
};

// ─── Timeline Visual ──────────────────────────────────────────────────────────

function TimelineVisual({ step }) {
  const { log, windowStart, windowEnd, currentEntry, cleanedEntries, t } = step;

  // Show ticks from 0 to max(t, 15) with some buffer
  const displayEnd = Math.max(t + 2, 15);
  const displayStart = 0;
  const range = displayEnd - displayStart;

  const toX = (tick) => ((tick - displayStart) / range) * 100;

  // Collect all entries ever seen
  const allEntries = [...log];

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60">
        <div className="flex items-center gap-2">
          <ScrollText className="w-3.5 h-3.5 text-emerald-500" />
          <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Sliding Window</span>
        </div>
        <span className="text-xs font-mono text-gray-400 dark:text-gray-500">t={t}, window=({windowStart},{windowEnd}]</span>
      </div>

      <div className="p-4">
        {/* Timeline */}
        <div className="relative h-16 mb-3">
          {/* Axis */}
          <div className="absolute bottom-4 left-0 right-0 h-px bg-gray-200 dark:bg-gray-700" />

          {/* Active window highlight */}
          {t > 0 && (
            <div
              className="absolute bottom-0 top-0 rounded bg-blue-100/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 transition-all duration-500"
              style={{
                left: `${toX(Math.max(windowStart, displayStart))}%`,
                right: `${100 - toX(Math.min(windowEnd, displayEnd))}%`,
              }}
            >
              <span className="absolute top-1 left-1 text-[9px] font-semibold text-blue-500 dark:text-blue-400 whitespace-nowrap">
                active window
              </span>
            </div>
          )}

          {/* Tick labels */}
          {Array.from({ length: displayEnd + 1 }, (_, i) => i).filter(i => i % 3 === 0).map(tick => (
            <div
              key={tick}
              className="absolute bottom-0 flex flex-col items-center"
              style={{ left: `${toX(tick)}%`, transform: 'translateX(-50%)' }}
            >
              <div className="w-px h-2 bg-gray-300 dark:bg-gray-600 mb-0.5" />
              <span className="text-[9px] text-gray-400 tabular-nums">{tick}</span>
            </div>
          ))}

          {/* Log entry dots */}
          {allEntries.map(entry => {
            const isCurrent = currentEntry && entry.id === currentEntry.id;
            return (
              <div
                key={entry.id}
                className={`absolute bottom-5 w-3 h-3 rounded-full border-2 transition-all duration-300 cursor-default
                  ${isCurrent
                    ? 'bg-blue-500 border-blue-600 scale-125 z-10'
                    : 'bg-emerald-500 border-emerald-600'}`}
                style={{ left: `${toX(entry.t)}%`, transform: 'translateX(-50%)' }}
                title={`${entry.label} at t=${entry.t}`}
              />
            );
          })}

          {/* Cleaned entry markers (faded out) */}
          {cleanedEntries.map(entry => (
            <div
              key={`exp-${entry.id}`}
              className="absolute bottom-5 w-3 h-3 rounded-full border-2 border-dashed bg-gray-200 dark:bg-gray-700 border-gray-400 dark:border-gray-500 opacity-40"
              style={{ left: `${toX(entry.t)}%`, transform: 'translateX(-50%)' }}
              title={`Expired: ${entry.label} at t=${entry.t}`}
            />
          ))}

          {/* Current time indicator */}
          {t > 0 && (
            <div
              className="absolute bottom-0 top-0 w-0.5 bg-blue-500 dark:bg-blue-400 opacity-60 transition-all duration-500"
              style={{ left: `${toX(t)}%` }}
            />
          )}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 text-[10px] text-gray-400 mb-4">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /> in-window</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" /> current request</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full border border-dashed border-gray-400 bg-gray-200 dark:bg-gray-700 inline-block opacity-60" /> expired (removed)</span>
          <span className="flex items-center gap-1"><span className="w-0.5 h-3 bg-blue-400 inline-block" /> current time</span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
            <p className="text-xl font-bold text-gray-700 dark:text-gray-200 tabular-nums">{step.totalRequests}</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">Requests</p>
          </div>
          <div className="text-center p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900">
            <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">{step.allowedCount}</p>
            <p className="text-[10px] text-emerald-500 uppercase tracking-wide mt-0.5">Allowed</p>
          </div>
          <div className="text-center p-2.5 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900">
            <p className="text-xl font-bold text-red-600 dark:text-red-400 tabular-nums">{step.rejectedCount}</p>
            <p className="text-[10px] text-red-500 uppercase tracking-wide mt-0.5">Rejected</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Log Panel ────────────────────────────────────────────────────────────────

function LogPanel({ step }) {
  const { log, windowStart, windowEnd, currentEntry, cleanedEntries } = step;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden flex flex-col">
      <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Active Log</span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500">timestamps in window</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-mono font-semibold tabular-nums ${log.length >= LIMIT ? 'text-red-500' : 'text-gray-500'}`}>
            {log.length} / {LIMIT}
          </span>
          <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${log.length >= LIMIT ? 'bg-red-500' : log.length >= LIMIT - 1 ? 'bg-amber-400' : 'bg-emerald-500'}`}
              style={{ width: `${(log.length / LIMIT) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" style={{ maxHeight: '200px', scrollbarWidth: 'thin' }}>
        {log.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-600 italic py-3 px-4">— log is empty —</p>
        ) : (
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-white dark:bg-gray-900 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-1.5 px-3 font-semibold text-gray-400 dark:text-gray-500">timestamp</th>
                <th className="text-left py-1.5 px-3 font-semibold text-gray-400 dark:text-gray-500">request</th>
                <th className="text-left py-1.5 px-3 font-semibold text-gray-400 dark:text-gray-500 whitespace-nowrap">expires at</th>
              </tr>
            </thead>
            <tbody>
              {log.map(entry => {
                const isCurrent = currentEntry && entry.id === currentEntry.id;
                const expiresAt = entry.t + WINDOW_SIZE;
                return (
                  <tr key={entry.id}
                    className={`border-b border-gray-50 dark:border-gray-800/50 transition-colors
                      ${isCurrent ? 'bg-blue-50 dark:bg-blue-950/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800/30'}`}>
                    <td className={`py-1.5 px-3 font-mono font-semibold tabular-nums ${isCurrent ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                      t={entry.t}
                    </td>
                    <td className={`py-1.5 px-3 font-mono truncate max-w-[130px] ${isCurrent ? 'text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400'}`}>
                      {entry.label}
                    </td>
                    <td className="py-1.5 px-3 font-mono text-gray-400 dark:text-gray-500 tabular-nums whitespace-nowrap">
                      t &gt; {expiresAt}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Cleanup section */}
      {cleanedEntries.length > 0 && (
        <div className="border-t border-gray-100 dark:border-gray-800 p-3 bg-amber-50 dark:bg-amber-950/20">
          <div className="flex items-center gap-1.5 mb-2">
            <Trash2 className="w-3 h-3 text-amber-500" />
            <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wide">
              Cleaned up ({cleanedEntries.length} expired)
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {cleanedEntries.map(entry => (
              <span key={entry.id} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-amber-200 dark:border-amber-800 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 line-through opacity-70">
                t={entry.t}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Explanation Card ─────────────────────────────────────────────────────────

function ExplanationCard({ step }) {
  const c = PHASE[step.phase] || PHASE.init;
  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-4`}>
      <div className="flex flex-wrap items-start gap-2 mb-2">
        <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${c.dot}`} />
        <span className={`text-sm font-bold ${c.text}`}>{step.title}</span>
        {step.badge && (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${c.badge}`}>{step.badge}</span>
        )}
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed ml-4">{step.explanation}</p>
    </div>
  );
}

// ─── Step Sidebar ─────────────────────────────────────────────────────────────

function StepSidebar({ steps, currentIdx, onJump }) {
  const activeRef = useRef(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentIdx]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest px-3 py-2 flex-shrink-0">Events</p>
      <div className="flex-1 overflow-y-auto space-y-0.5 px-2 pb-2" style={{ scrollbarWidth: 'thin' }}>
        {steps.map((s, i) => {
          const c = PHASE[s.phase] || PHASE.init;
          const isCurrent = i === currentIdx;
          const isPast = i < currentIdx;
          return (
            <button
              key={i}
              ref={isCurrent ? activeRef : null}
              onClick={() => onJump(i)}
              className={`w-full text-left px-2.5 py-2 rounded-lg flex items-center gap-2 text-[11px] transition-all duration-150
                ${isCurrent ? `${c.bg} border ${c.border} font-semibold`
                  : isPast ? 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 dark:text-gray-500'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 text-gray-300 dark:text-gray-600'}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isCurrent ? c.dot : isPast ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
              <span className={`font-mono leading-tight truncate ${isCurrent ? c.text : ''}`}>
                <span className="text-gray-300 dark:text-gray-600 mr-1">{String(i + 1).padStart(2, '0')}.</span>
                {s.title}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SlidingWindowLog() {
  const [idx, setIdx] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const total = STEPS.length;
  const step = STEPS[idx];

  const goTo = (i) => {
    setIdx(Math.max(0, Math.min(total - 1, i)));
    setAnimKey(k => k + 1);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { setIdx(i => Math.min(i + 1, total - 1)); setAnimKey(k => k + 1); }
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   { setIdx(i => Math.max(i - 1, 0));          setAnimKey(k => k + 1); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [total]);

  const c = PHASE[step.phase] || PHASE.init;

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-gray-950 text-gray-900 dark:text-white overflow-hidden">

      {/* Top bar */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-950/90 backdrop-blur-sm z-10">
        <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/dev" className="flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Dev</span>
            </Link>
            <span className="text-gray-300 dark:text-gray-700">/</span>
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Sliding Window Log — Rate Limiting Simulator</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 tabular-nums">{idx + 1} / {total}</span>
            <div className="hidden sm:flex w-28 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500" style={{ width: `${((idx + 1) / total) * 100}%` }} />
            </div>
            <span className="hidden sm:inline text-xs text-gray-400">← → keys</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        <div className="hidden lg:flex flex-col w-52 xl:w-60 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 overflow-hidden">
          <StepSidebar steps={STEPS} currentIdx={idx} onJump={goTo} />
        </div>

        <div className="flex-1 overflow-y-auto">
          <div key={animKey} className="max-w-4xl mx-auto px-4 py-5 space-y-5" style={{ animation: 'fadeSlideIn 0.25s ease' }}>
            <ExplanationCard step={step} />
            <TimelineVisual step={step} />
            <LogPanel step={step} />
            <div className="h-4" />
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm z-10">
        <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <button onClick={() => goTo(idx - 1)} disabled={idx === 0}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <div className="hidden lg:flex items-center gap-2 text-xs text-gray-400">
            {step.badge && <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${c.badge}`}>{step.badge}</span>}
            <span className="font-mono">{step.title}</span>
          </div>
          <div className="flex lg:hidden items-center gap-2 text-xs text-gray-400">
            <div className="w-32 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500" style={{ width: `${((idx + 1) / total) * 100}%` }} />
            </div>
            <span className="tabular-nums">{idx + 1}/{total}</span>
          </div>
          <button onClick={() => goTo(idx + 1)} disabled={idx === total - 1}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-transparent bg-blue-600 hover:bg-blue-700 text-sm font-semibold text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <style>{`@keyframes fadeSlideIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
